import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { corporateTheme, corporateLabels } from '../themes/corporate';

const TestimonialsContainer = styled.div`
  width: 100%;
  padding: 6rem 2rem;
  background: ${corporateTheme.backgroundGradient};
  position: relative;
  overflow: hidden;
  font-family: ${corporateTheme.fontFamily};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: ${corporateTheme.textSecondary};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  /* 4 cards: 2x2 layout for better balance */
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (max-width: 1000px) {
    /* Switch to auto-fit for medium screens */
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TestimonialCard = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(15px);
  border-radius: 25px;
  padding: 3rem;
  border: 1px solid ${corporateTheme.cardBorder};
  position: relative;
  overflow: hidden;
  box-shadow: ${corporateTheme.cardShadow};
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${corporateTheme.primaryGradient};
  }
  
  &:hover {
    background: rgba(30, 64, 175, 0.05);
    border-color: rgba(30, 64, 175, 0.2);
  }
`;

const QuoteIcon = styled.div`
  font-size: 3rem;
  color: ${corporateTheme.primary};
  opacity: 0.3;
  margin-bottom: 1rem;
`;

const TestimonialText = styled.p`
  font-size: 1.1rem;
  color: ${corporateTheme.textSecondary};
  line-height: 1.7;
  margin-bottom: 2rem;
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${corporateTheme.primaryGradient};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  color: ${corporateTheme.background};
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorName = styled.div`
  font-size: 1.1rem;
  color: ${corporateTheme.textPrimary};
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const AuthorTitle = styled.div`
  font-size: 0.9rem;
  color: ${corporateTheme.primary};
  font-weight: 500;
`;

const AuthorEarnings = styled.div`
  font-size: 0.8rem;
  color: ${corporateTheme.textTertiary};
  margin-top: 0.25rem;
`;

const FeaturedTestimonial = styled(motion.div)`
  background: linear-gradient(135deg, rgba(30, 64, 175, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%);
  backdrop-filter: blur(20px);
  border-radius: 30px;
  padding: 4rem;
  text-align: center;
  border: 1px solid rgba(30, 64, 175, 0.2);
  position: relative;
  overflow: hidden;
  margin-top: 2rem;
  box-shadow: ${corporateTheme.cardShadow};
`;

const FeaturedQuote = styled.div`
  font-size: 1.8rem;
  color: ${corporateTheme.textPrimary};
  line-height: 1.6;
  margin-bottom: 2rem;
  font-style: italic;
  font-weight: 300;
`;

const FeaturedAuthor = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const FeaturedAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${corporateTheme.primaryGradient};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  color: ${corporateTheme.background};
`;

const FeaturedAuthorInfo = styled.div`
  text-align: left;
  
  @media (max-width: 768px) {
    text-align: center;
  }
`;

const FeaturedName = styled.div`
  font-size: 1.5rem;
  color: ${corporateTheme.textPrimary};
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const FeaturedTitle = styled.div`
  font-size: 1.1rem;
  color: ${corporateTheme.primary};
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const FeaturedEarnings = styled.div`
  font-size: 1rem;
  color: ${corporateTheme.textSecondary};
`;

const TestimonialsSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const testimonials = [
    {
      text: "GrowTech completely transformed my affiliate business. Their e-commerce offers convert at 3x the rate of other networks, and the support team is incredibly responsive. I've tripled my monthly earnings in just 6 months.",
      author: "Chen Wei",
      title: "Professional Affiliate Marketer",
      earnings: "€45,000+ monthly revenue",
      avatar: "CW"
    },
    {
      text: "The SaaS campaigns from GrowTech are phenomenal. The conversion rates are consistently high, and they always pay on time. Their understanding of the Asian market is unmatched.",
      author: "Kenji Tanaka",
      title: "Digital Marketing Specialist",
      earnings: "€32,000+ monthly revenue",
      avatar: "KT"
    },
    {
      text: "I've worked with many affiliate networks, but GrowTech stands out. Their technology platform is user-friendly, tracking is accurate, and the revenue share rates are the best I've found.",
      author: "Sarah Kim",
      title: "E-commerce Content Creator",
      earnings: "€28,000+ monthly revenue",
      avatar: "SK"
    },
    {
      text: "The account management at GrowTech is exceptional. They provide personalized strategies and always help optimize my campaigns. My conversion rates improved by 40% since joining.",
      author: "David Park",
      title: "Affiliate Network Manager",
      earnings: "€55,000+ monthly revenue",
      avatar: "DP"
    }
  ];

  const featuredTestimonial = {
    text: "GrowTech isn't just an affiliate network - they're true partners. Their deep understanding of Asian digital markets, combined with cutting-edge technology and genuine support, has helped me build a seven-figure affiliate business. The quality of their e-commerce and SaaS offers is unmatched.",
    author: "Michael Zhang",
    title: "Senior Affiliate Marketing Director",
    earnings: "€120,000+ monthly revenue",
    avatar: "MZ"
  };

  return (
    <TestimonialsContainer ref={ref} id="testimonials">
      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <Header>
            <Title variants={itemVariants}>
              What Our Partners Say
            </Title>
            <Subtitle variants={itemVariants}>
              Real success stories from affiliates who've grown their business with GrowTech
            </Subtitle>
          </Header>

          <TestimonialsGrid>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <QuoteIcon>"</QuoteIcon>
                <TestimonialText>{testimonial.text}</TestimonialText>
                <TestimonialAuthor>
                  <AuthorAvatar>{testimonial.avatar}</AuthorAvatar>
                  <AuthorInfo>
                    <AuthorName>{testimonial.author}</AuthorName>
                    <AuthorTitle>{testimonial.title}</AuthorTitle>
                    <AuthorEarnings>{testimonial.earnings}</AuthorEarnings>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            ))}
          </TestimonialsGrid>

          <FeaturedTestimonial variants={itemVariants}>
            <QuoteIcon>"</QuoteIcon>
            <FeaturedQuote>{featuredTestimonial.text}</FeaturedQuote>
            <FeaturedAuthor>
              <FeaturedAvatar>{featuredTestimonial.avatar}</FeaturedAvatar>
              <FeaturedAuthorInfo>
                <FeaturedName>{featuredTestimonial.author}</FeaturedName>
                <FeaturedTitle>{featuredTestimonial.title}</FeaturedTitle>
                <FeaturedEarnings>{featuredTestimonial.earnings}</FeaturedEarnings>
              </FeaturedAuthorInfo>
            </FeaturedAuthor>
          </FeaturedTestimonial>
        </motion.div>
      </Container>
    </TestimonialsContainer>
  );
};

export default TestimonialsSection; 